import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gauge, Loader2, Save } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

// Default active-job capacity for workers (blank = unlimited). Per-worker overrides live in Team.
export const CapacityCard = () => {
  const [cap, setCap] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/system`, { headers: authHeaders() })
      .then((r) => { setCap(r.data.default_capacity ?? ""); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const val = cap === "" ? null : Math.max(1, Math.min(50, parseInt(cap, 10) || 1));
      await axios.put(`${API}/admin/system`, { default_capacity: val }, { headers: authHeaders() });
      toast.success(val ? `Workers can hold at most ${val} active job(s) by default.` : "Default capacity removed — unlimited.");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="capacity-card" className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 sm:p-8">
      <div className="flex items-center gap-2.5">
        <Gauge className="h-5 w-5 text-cyan-400" />
        <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Worker capacity</h2>
      </div>
      <p className="mt-2 text-xs text-[var(--pv-text-4)]">
        Maximum jobs a worker can hold at once (active, awaiting review or in revision). Leave blank for unlimited.
        Per-worker overrides are set on their Team profile.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <input
          data-testid="capacity-input"
          type="number"
          min="1"
          max="50"
          value={cap}
          disabled={!loaded}
          onChange={(e) => setCap(e.target.value)}
          placeholder="Unlimited"
          className="w-36 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10"
        />
        <button data-testid="capacity-save-btn" onClick={save} disabled={saving || !loaded} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </button>
      </div>
    </div>
  );
};
